// Convert plain text to Quill-like HTML
// Add this helper function inside your ModalTextArea component (or outside if you prefer)
export const plainTextToQuillHtml = (text: string) => {
  if (!text) return "<br>";
  // Remove trailing newlines and spaces
  text = text.replace(/[\s\n\r]+$/g, "");
  // Escape HTML special chars
  const escapeHtml = (str: string) =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  // Replace tabs with 4 spaces
  let html = escapeHtml(text).replace(/\t/g, "    ");
  // Replace all line breaks with <br>
  html = html.replace(/\r\n|\r|\n/g, "<br>");
  // Remove any existing <p> or </p> tags to avoid nested paragraphs
  html = html.replace(/<\/?p[^>]*>/gi, "");
  // If the result is empty after trimming, return <br>
  if (!html.trim()) return "<br>";
  // Wrap in a single <p>
  html = `<p>${html}</p>`;
  return html;
};

export const normalizeHtml = (html: string) => {
  if (!html) return "";
  let norm = html;

  // Replace <li> with a marker (e.g. "- " for ul, "1. " for ol)
  // We'll use a placeholder for ol/ul detection
  norm = norm.replace(/<ul[^>]*>/gi, '[[UL]]');
  norm = norm.replace(/<ol[^>]*>/gi, '[[OL]]');
  norm = norm.replace(/<\/ul>/gi, '[[/UL]]');
  norm = norm.replace(/<\/ol>/gi, '[[/OL]]');

  // Replace <li> and </li> with line breaks and markers
  norm = norm.replace(/<li[^>]*>/gi, '[[LI]]');
  norm = norm.replace(/<\/li>/gi, '[[/LI]]');

  // Replace <p> and </p> with line breaks
  norm = norm.replace(/<p[^>]*>/gi, '');
  norm = norm.replace(/<\/p>/gi, '[[BR]]');

  // Remove all tags except <b>, <strong>, <i>, <em>, <u>
  norm = norm.replace(/<(?!\/?(b|strong|i|em|u)\b)[^>]+>/gi, '');

  // Decode HTML entities
  const txt = document.createElement('textarea');
  txt.innerHTML = norm;
  norm = txt.value;

  // Now, convert our placeholders to readable text
  // For ordered lists, use "1. ", for unordered use "- "
  norm = norm.replace(/\[\[UL\]\]([\s\S]*?)\[\[\/UL\]\]/g, (m, content) =>
      content.replace(/\[\[LI\]\]([\s\S]*?)\[\[\/LI\]\]/g, '- $1[[BR]]')
  );
  norm = norm.replace(/\[\[OL\]\]([\s\S]*?)\[\[\/OL\]\]/g, (m, content) =>
      content.replace(/\[\[LI\]\]([\s\S]*?)\[\[\/LI\]\]/g, (mm:any, liContent:any, idx:any) => {
          // Optionally, you can number them, but for diff, just use "1. "
          return '1. ' + liContent + '[[BR]]';
      })
  );

  // Remove any remaining [[LI]] or [[/LI]]
  norm = norm.replace(/\[\[LI\]\]/g, '').replace(/\[\[\/LI\]\]/g, '');

  // Collapse multiple [[BR]]
  norm = norm.replace(/(\[\[BR\]\]\s*){2,}/g, '[[BR]]');
  norm = norm.replace(/^\[\[BR\]\]+|\[\[BR\]\]+$/g, ''); // trim leading/trailing [[BR]]
  norm = norm.replace(/ +/g, " "); // collapse multiple spaces
  norm = norm.trim();

  return norm;
};

export const htmlToNormalizedPlainText = (html: string) => {
  if (!html) return "";
  // Replace <br> and <br/> with \n
  let text = html.replace(/<br\s*\/?>/gi, "\n");
  // Replace </p><p> (with or without whitespace) with \n
  text = text.replace(/<\/p>\s*<p[^>]*>/gi, "\n");
  // Remove all <p> tags
  text = text.replace(/<p[^>]*>/gi, "");
  // Replace </p> with \n
  text = text.replace(/<\/p>/gi, "\n");
  // Remove all other tags
  text = text.replace(/<[^>]+>/g, "");
  // Decode HTML entities
  const div = document.createElement("div");
  div.innerHTML = text;
  text = div.textContent || div.innerText || "";
  // Normalize line endings, remove extra blank lines
  text = text.replace(/\r\n|\r/g, "\n");
  text = text.replace(/\n{2,}/g, "\n");
  // Trim spaces at the end of each line and collapse multiple spaces
  text = text
    .split("\n")
    .map((line) => line.replace(/\s+$/, "").replace(/ {2,}/g, " ")) // trim end, collapse multiple spaces
    .join("\n");
  // Collapse multiple spaces globally (in case of leading spaces)
  text = text.replace(/ {2,}/g, " ");
  return text.trim();
};

export const highlightHtml = (html: string, search: string): string => {
  if (!search || !html) return html;
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  let idx = 0;
  return html.replace(
    new RegExp(escaped, "gi"),
    (match) =>
      `<span class="search-match" data-match-idx="${idx++}" style="background:yellow;color:black;">${match}</span>`
  );
};

export const sanitizeAllowedHtmlForSubmit = (html: string): string => {
  if (!html) return "";

  // Keep plain text untouched, including pseudo-tag typing like "<p <strong <br".
  if (!/<\/?[a-z][^>]*>/i.test(html)) {
    return html;
  }

  const allowedTagNames = new Set(["P", "BR", "STRONG", "EM", "U", "OL", "LI", "UL"]);
  const parser = new DOMParser();
  const parsed = parser.parseFromString(html, "text/html");

  const sanitizeNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tagName = element.tagName;

      if (!allowedTagNames.has(tagName)) {
        const parent = element.parentNode;
        if (parent) {
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          parent.removeChild(element);
        }
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        element.removeAttribute(attribute.name);
      });
    }

    Array.from(node.childNodes).forEach((childNode) => sanitizeNode(childNode));
  };

  Array.from(parsed.body.childNodes).forEach((node) => sanitizeNode(node));
  return parsed.body.innerHTML;
};



// WEBPACK FOOTER //
// ./src/components/Table/components/twoLanguage/EditorUtil.ts